import { ENEMY } from '../config/config';
import { sign } from '../core/math';
import type { World } from '../game/world';
import { C } from '../gfx/palette';
import type { Renderer } from '../gfx/renderer';
import { moveBody } from '../world/physics';
import { Enemy } from './enemy';

export type SnugState = 'walk' | 'shell' | 'slide';

/**
 * Snug — an armoured walker. Stomping it pops it into its shell; touching the shell
 * kicks it into a high-speed slide that mows down anything in its path (and rebounds
 * off walls). Stomping a sliding shell brings it to a halt again.
 */
export class Snug extends Enemy {
  state: SnugState = 'walk';
  private anim = 0;
  private reviveTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, 16, 16);
    this.speed = ENEMY.snugSpeed;
    this.turnAtEdge = true;
  }

  protected override get particleColor(): string {
    return C.shell;
  }

  /** Sliding shells are dangerous to enemies and to the player. */
  get sliding(): boolean {
    return this.state === 'slide';
  }

  override update(dt: number, world: World): void {
    if (this.updateTumble(dt, world)) return;
    this.anim += dt;

    if (this.state === 'walk') {
      this.patrol(dt, world);
    } else if (this.state === 'shell') {
      this.vx = 0;
      this.vy = Math.min(this.vy + 900 * dt, 280);
      moveBody(this, world.map, dt, world.solidBoxes());
      if (ENEMY.shellReviveTime > 0) {
        this.reviveTimer += dt;
        if (this.reviveTimer >= ENEMY.shellReviveTime) this.revive();
      }
    } else {
      this.vx = this.facing * ENEMY.shellKickSpeed;
      this.vy = Math.min(this.vy + 900 * dt, 300);
      const res = moveBody(this, world.map, dt, world.solidBoxes());
      if (this.hitLeft || this.hitRight) {
        const dir = this.hitLeft ? 1 : -1;
        this.facing = dir;
        world.sfx('bump');
        world.shake(1.2);
        // A speeding shell knocks on the blocks it slams into.
        for (const h of res.hits) {
          if (h.side === 'left' || h.side === 'right') {
            world.hitBlock(h.tx, h.ty, 'shell', true);
          }
        }
      }
      if (world.rng.bool(0.4)) {
        world.particles.dust(this.centerX, this.bottom - 1, this.facing);
      }
    }

    if (this.y > world.map.pixelH + 48) this.alive = false;
  }

  private toShell(): void {
    const bottom = this.bottom;
    this.state = 'shell';
    this.h = 12;
    this.y = bottom - this.h;
    this.vx = 0;
    this.reviveTimer = 0;
  }

  private revive(): void {
    const bottom = this.bottom;
    this.state = 'walk';
    this.h = 16;
    this.y = bottom - this.h;
    this.reviveTimer = 0;
    this.dying = false;
  }

  /** Kick the shell; `dir` is the travel direction. */
  kick(world: World, dir: number): void {
    this.state = 'slide';
    this.facing = dir === 0 ? 1 : sign(dir);
    this.reviveTimer = 0;
    world.sfx('kick');
    world.particles.puff(this.centerX, this.centerY, C.shellLight, 5);
  }

  override onStomp(world: World): boolean {
    if (this.tumbling) return false;
    if (this.state === 'walk') {
      this.toShell();
      world.addScore(world.nextStompScore(), this.centerX, this.y);
      world.sfx('stomp');
      world.particles.puff(this.centerX, this.bottom - 3, C.shellLight, 6);
      return true;
    }
    if (this.state === 'slide') {
      this.state = 'shell';
      this.vx = 0;
      this.reviveTimer = 0;
      world.sfx('stomp');
      return true;
    }
    // idle shell: a stomp kicks it away from the player
    const dir = world.entities.length ? sign(this.centerX - world.playerCenterX) || 1 : 1;
    this.kick(world, dir);
    world.addScore(world.nextStompScore(), this.centerX, this.y);
    return true;
  }

  override onOrb(world: World): boolean {
    this.defeatFlip(world, 200, this.facing);
    return true;
  }

  override onShellHit(world: World, dir: number): void {
    this.defeatFlip(world, world.nextShellScore(), dir);
  }

  override onBlockBump(world: World): void {
    this.defeatFlip(world, 200, this.facing);
  }

  override onPrism(world: World): void {
    this.defeatFlip(world, 200, this.facing);
  }

  override draw(r: Renderer): void {
    if (this.tumbling) {
      this.drawTumbling(r, 'shell.0');
      return;
    }
    if (this.state === 'walk') {
      const frame = Math.floor(this.anim * 6) % 2;
      r.sprite(`snug.${frame}`, this.x, this.y, this.facing > 0);
      return;
    }
    if (this.state === 'shell') {
      const wobble = this.reviveTimer > ENEMY.shellReviveTime - 1.5 ? Math.floor(this.anim * 14) % 2 : 0;
      r.sprite(`shell.${wobble}`, this.x, this.y, this.facing > 0);
      return;
    }
    const frame = 1 + (Math.floor(this.anim * 26) % 2);
    r.sprite(`shell.${frame}`, this.x, this.y, this.facing > 0);
  }
}
